import { SharedModule } from '@shared/shared.module';
import { Injectable, NgModule, inject, provideAppInitializer } from '@angular/core';
import {
  BrowserModule,
  HAMMER_GESTURE_CONFIG,
  HammerGestureConfig,
  HammerModule
} from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  HttpClient,
  HTTP_INTERCEPTORS,
  HttpBackend,
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import { RegisterComponent } from '@modules/public/register/register.component';
import { LoginComponent } from '@modules/public/login/login.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LoginFormComponent } from '@modules/public/login/components/login-form/login-form.component';
import { AccountNotVerifiedComponent } from '@modules/public/login/components/account-not-verified/account-not-verified.component';
import { RegisterFormComponent } from '@modules/public/register/components/register-form/register-form.component';
import { VerifyingEmailComponent } from '@modules/public/register/components/verifying-email/verifying-email.component';
import { SignInMobileFooterComponent } from '@modules/public/register/components//sign-in-mobile-footer/sign-in-mobile-footer.component';
import { LoadingInterceptor } from '@core/interceptors/loading.interceptor';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RememberComponent } from '@modules/public/remember/remember.component';
import { RememberFormComponent } from '@modules/public/remember/components/remember-form/remember-form.component';
import { ActiveComponent } from '@modules/public/active/active.component';
import { AccountActivatedComponent } from '@modules/public/active/components/account-activated/account-activated.component';
import { ActivationLinkExpiredComponent } from '@modules/public/active/components/activation-link-expired/activation-link-expired.component';
import { ResendVerificationLinkFormComponent } from '@modules/public/active/components/resend-verification-link-form/resend-verification-link-form.component';

import { IonicModule } from '@ionic/angular';
import { ErrorInterceptor } from '@core/interceptors/error.interceptor';
import { MessageService } from 'primeng/api';
import { HomeVideoComponent } from '@modules/public/home-video/home-video.component';
import { SocialMediaDetailPublicComponent } from '@modules/public/social-media-detail-public/social-media-detail-public.component';
import { APP_CONFIG, APP_CONSTANTS } from './app.config';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { routerReducer, StoreRouterConnectingModule } from '@ngrx/router-store';
import privateReducer from '@modules/private/features/private.slice';
import userReducer, { loadUser, selectUser } from '@core/modules/user/features/user.slice';
import { CoreModule } from '@core/core.module';
import { DatePipe, DecimalPipe, isPlatformBrowser } from '@angular/common';
import { environment } from '@env';
import { Observable } from 'rxjs';
import { TokenStorageService } from '@core/services/token-storage.service';
import { filter, first } from 'rxjs/operators';
import { Router, TitleStrategy } from '@angular/router';
import languageReducer, { loadLanguages } from '@core/modules/language/features/language.slice';
import {
  HttpLoaderFactory,
  MyMissingTranslationHandler
} from '@core/modules/language/language.module';
import { provideTranslateService } from '@ngx-translate/core';
import { TranslateModule, TranslateLoader, MissingTranslationHandler } from '@ngx-translate/core';
import captivePortalReducer from '@core/modules/user/pages/captive-portal/features/captive-portal.slice';
import masterDataReducer from '@core/modules/master-data/features/master-data.slice';
import { loadCountries } from '@core/modules/countries/features/countries.slice';
import * as Hammer from 'hammerjs';
import profileReducer from '@core/modules/user/pages/profile/features/profile.slice';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MAT_RIPPLE_GLOBAL_OPTIONS,
  RippleGlobalOptions
} from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter
} from '@angular/material-moment-adapter';
import { PageTitleService } from '@shared/services/page.title.service';
import { PromoBannersContainerComponent } from '@shared/components/promo-banners/promo-banners-container.component';
import { DIALOG_MODULE_CONFIG, DIALOG_MODULE_CONSTANTS } from '@shared/dialog/dialog.module.config';
import { SUPPORT_CONFIG, SUPPORT_CONSTANTS } from '@modules/private/support/support.config';
import { providePrimeNG } from 'primeng/config';
import PrimeNGTheme from '@primeng/themes/aura';

function initializeAppFactory(
  tokenStorageService: TokenStorageService,
  store: Store
): () => Observable<any> {
  return () => {
    const token = tokenStorageService.getToken();
    store.dispatch(loadLanguages());
    store.dispatch(loadCountries());
    !!token && store.dispatch(loadUser(token.user.email));
    return store.select(selectUser).pipe(
      filter((user) => !!user || !token),
      first()
    );
  };
}

const globalRippleConfig: RippleGlobalOptions = {
  disabled: true,
  animation: {
    enterDuration: 0,
    exitDuration: 0
  }
};

@Injectable()
export class HammerConfig extends HammerGestureConfig {
  overrides = <any>{
    swipe: { direction: Hammer.DIRECTION_ALL }
  };
}

export function httpTranslateLoader(http: HttpClient, platformId: any) {
  return new TranslateHttpLoader(
    http,
    isPlatformBrowser(platformId) ? './assets/i18n/' : `${environment.baseURL}assets/i18n/`,
    '.json'
  );
}

export interface RootState {
  router: ReturnType<typeof routerReducer>;
  user: ReturnType<typeof userReducer>;
  masterData: ReturnType<typeof masterDataReducer>;
  captivePortal: ReturnType<typeof captivePortalReducer>;
  profile: ReturnType<typeof profileReducer>;
  language: ReturnType<typeof languageReducer>;
  private: ReturnType<typeof privateReducer>;
}

@NgModule({
  declarations: [
    AppComponent,
    RegisterComponent,
    HomeVideoComponent,
    LoginComponent,
    LoginFormComponent,
    AccountNotVerifiedComponent,
    RegisterFormComponent,
    VerifyingEmailComponent,
    SignInMobileFooterComponent,
    RememberComponent,
    RememberFormComponent,
    ActiveComponent,
    AccountActivatedComponent,
    ActivationLinkExpiredComponent,
    ResendVerificationLinkFormComponent,
    SocialMediaDetailPublicComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    CoreModule,
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    IonicModule.forRoot(),
    // TranslateModule.forRoot({
    //   defaultLanguage: APP_CONSTANTS.DEFAULT_LANGUAGE_LOCALE,
    //   loader: {
    //     provide: TranslateLoader,
    //     useFactory: HttpLoaderFactory,
    //     deps: [HttpBackend]
    //   },
    //   missingTranslationHandler: {
    //     provide: MissingTranslationHandler,
    //     useClass: MyMissingTranslationHandler
    //   }
    // }),
    // TranslateModule.forChild({
    //   loader: {
    //     provide: TranslateLoader,
    //     useFactory: httpTranslateLoader,
    //     deps: [HttpClient]
    //   }
    // }),
    StoreModule.forRoot({
      router: routerReducer
    }),
    EffectsModule.forRoot([]),
    StoreDevtoolsModule.instrument({
      maxAge: 25
    }),
    StoreRouterConnectingModule.forRoot(),
    HammerModule,
    PromoBannersContainerComponent
  ],
  providers: [
    providePrimeNG({
      theme: {
        preset: PrimeNGTheme,
        options: {
          darkModeSelector: false
        }
      }
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeAppFactory(inject(TokenStorageService), inject(Store));
      return initializerFn();
    }),
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerConfig
    },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: APP_CONFIG, useValue: APP_CONSTANTS },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    {
      provide: MAT_DATE_LOCALE,
      useValue: APP_CONSTANTS.DEFAULT_LANGUAGE_LOCALE
    },
    {
      provide: TitleStrategy,
      useClass: PageTitleService
    },
    {
      provide: DIALOG_MODULE_CONFIG,
      useValue: DIALOG_MODULE_CONSTANTS
    },
    { provide: SUPPORT_CONFIG, useValue: SUPPORT_CONSTANTS },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
    { provide: MAT_DATE_FORMATS, useValue: APP_CONSTANTS.CUSTOM_DATE_FORMAT },
    DecimalPipe,
    DatePipe,
    MessageService,
    provideHttpClient(withInterceptorsFromDi()),
    provideTranslateService({
      defaultLanguage: APP_CONSTANTS.DEFAULT_LANGUAGE_LOCALE,
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend]
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MyMissingTranslationHandler
      }
    })
  ]
})
export class AppModule {}
